import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { type StripeEmbeddedCheckout } from '@stripe/stripe-js'
import { getStripe } from '../../../lib/stripe'
import { translate } from '../../../lib/i18n'
import { formatUsd } from '../../../lib/planPricing'


/**
 * Stripe Checkout, mounted inside our own page instead of a Stripe domain.
 *
 * The form itself is still Stripe's — that is deliberate. It keeps
 * subscriptions, promo codes, wallets, 3-D Secure and translations working
 * without us reimplementing any of them; the only thing this changes is that
 * the customer never gets bounced to a page with someone else's branding
 * halfway through paying.
 */

/** Stripe permits one embedded checkout per page; these serialise access to it. */
let active: StripeEmbeddedCheckout | null = null
let checkoutQueue: Promise<unknown> = Promise.resolve()

/**
 * Lo que se enseña al lado del formulario de pago.
 *
 * Es neutro a propósito. Antes hablaba de planes —precio al mes, ciclo, «volver
 * a planes»— y al empezar a vender dominios el mismo panel decía «/mes» de algo
 * que se cobra una vez al año. Quien abre el pago arma estos textos; aquí sólo
 * se pintan.
 */
export type CheckoutSummary = {
  /** Lo que se compra: el nombre del plan, o el dominio. */
  title: string
  description: string
  features: string[]
  /** El importe grande, ya con el descuento que haya. */
  amount: number
  /** Lo que va detrás del importe: `USD/mes`, `USD/año`. */
  unit: string
  /** La línea de debajo: cada cuánto se cobra y cuánto se paga hoy. */
  billed: string
  /**
   * No enseñar el precio a la izquierda. Stripe ya lo dice, entero y con lo que
   * viene después, en el formulario de la derecha, y verlo dos veces con
   * formatos distintos hacía dudar de cuál se cobraba. Se queda la etiqueta de
   * la promoción y su nota.
   */
  hidePrice?: boolean | undefined
  /** La etiqueta de una promoción vigente. */
  badge?: string | undefined
  /** Lo que explica la promoción, sin cifras. */
  note?: string | undefined
  /** Precio sin descuento, tachado. Se omite cuando no hay promoción. */
  listAmount?: number | undefined
  /** A dónde vuelve el botón de arriba. */
  backLabel: string
  /** La letra pequeña, que no dice lo mismo en una suscripción que en un dominio. */
  legal: ReactNode
}

export function EmbeddedCheckoutPanel({
  clientSecret,
  summary,
  onClose,
  onComplete,
}: {
  clientSecret: string
  summary: CheckoutSummary
  onClose: () => void
  /** Stripe reports the payment here; the session never redirects. */
  onComplete: () => void
}) {
  const mountRef = useRef<HTMLDivElement>(null)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    /**
     * Creation is queued, not just guarded.
     *
     * Stripe allows exactly one embedded checkout at a time, and creating it is
     * async. React's StrictMode mounts, unmounts and mounts again immediately,
     * so the second attempt started while the first was still resolving —
     * neither existed yet to be destroyed, and Stripe refused the pair with
     * "You cannot have multiple Embedded Checkout objects". Chaining every
     * create and destroy through one promise makes the overlap impossible
     * rather than unlikely.
     */
    const run = checkoutQueue.then(async () => {
      if (cancelled) return
      const loader = getStripe()
      if (!loader) {
        setError(translate('embeddedCheckout.missingKey'))
        return
      }
      const instance = await loader
      if (cancelled || !instance) return

      active?.destroy()
      active = null

      const checkout = await instance.createEmbeddedCheckoutPage({
        clientSecret,
        // Read through a ref: Stripe keeps the callback it was given, and this
        // effect must not re-run (and rebuild the form) when the parent
        // re-renders with a new function identity.
        onComplete: () => onCompleteRef.current(),
      })
      // Dismissed while Stripe.js was loading: mounting now would attach a form
      // to a node that is already gone, and leave the instance alive.
      if (cancelled || !mountRef.current) { checkout.destroy(); return }
      active = checkout
      checkout.mount(mountRef.current)
    }).catch((cause: unknown) => {
      if (!cancelled) setError(cause instanceof Error ? cause.message : translate('embeddedCheckout.loadFailed'))
    })

    checkoutQueue = run
    return () => {
      cancelled = true
      // Waits for the in-flight create: destroying before it resolves would
      // miss the instance and strand it.
      checkoutQueue = run.then(() => { active?.destroy(); active = null })
    }
  }, [clientSecret])

  /**
   * Portalled to <body>: rendered in place it inherits whatever stacking
   * context and containing block its ancestors impose, so `position: fixed`
   * stopped meaning "the viewport" and the dashboard chrome stayed on top.
   */
  return createPortal(
    <div className="checkout-page">
      <div className="checkout-columns">
        {/* The rail is what sticks; the summary just sits centred inside it. */}
        <div className="checkout-summary-rail">
        <aside className="checkout-summary">
          {/* Inside the column rather than floating over the page: it belongs to
              what is being bought, and it travels with the summary when the
              sticky block moves. */}
          <button type="button" className="checkout-back" onClick={onClose}>
            <span aria-hidden="true">←</span> {summary.backLabel}
          </button>

          {/* Mark and plan as one lockup: Stripe's form carries no branding of
              ours, so this is the only place the paying screen says whose
              checkout it is. */}
          <div className="checkout-brand-row">
            <img className="checkout-brand" src="/veltica.svg" alt="véltica" />
            <h2 className="checkout-summary-name">{summary.title}</h2>
          </div>
          <p className="checkout-summary-desc">{summary.description}</p>

          {/* The monthly figure is what people compare; the charge is what they
              are about to authorise, so an annual plan states both. */}
          {summary.hidePrice ? (
            summary.badge ? (
              <div className="checkout-summary-promo">
                <span className="checkout-summary-badge">{summary.badge}</span>
                {summary.note ? <p>{summary.note}</p> : null}
              </div>
            ) : null
          ) : (
            <div className="checkout-summary-pricebox">
              {summary.badge ? <span className="checkout-summary-badge">{summary.badge}</span> : null}
              <div className="checkout-summary-price">
                {summary.listAmount !== undefined && summary.listAmount !== summary.amount ? (
                  <s>${formatUsd(summary.listAmount)}</s>
                ) : null}
                <strong>${formatUsd(summary.amount)}</strong>
                <span>{summary.unit}</span>
              </div>
              <p className="checkout-summary-billed">{summary.billed}</p>
            </div>
          )}

          <ul className="checkout-summary-features">
            {summary.features.map((feature) => <li key={feature}>{feature}</li>)}
          </ul>

          {/* Same wording and the same two links as the login screen, so the
              terms someone accepts at checkout are visibly the ones they
              already agreed to — and they open in a new tab, because losing a
              half-filled payment form to read them would be its own problem. */}
          <p className="checkout-summary-legal">{summary.legal}</p>
        </aside>
        </div>

        <section className="checkout-form">
          {error ? (
            <p className="checkout-panel-error">{error}</p>
          ) : (
            <div className="checkout-panel-body" ref={mountRef} />
          )}
        </section>
      </div>
    </div>,
    document.body,
  )
}
